import React, { useState } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from '../components/rn';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import ProfileSection from '../components/ProfileSection';
import { useTranslation } from '../i18n/I18nContext';
import { useTheme } from '../theme/ThemeContext';
import { fontSize, spacing } from '../theme';
import { profilePalette } from '../theme/profileSection';
import GoogleLinkScreen from './GoogleLinkScreen';
import { googleSignInEnabled } from '../auth/googleAuth';
import { InfoButton, InfoText, useInfo } from '../components/InfoHint';

// An informational scaffold, not a claim that a provider is connected.
// Identity verification remains distinct from account authentication.
export default function SignInMethodsScreen({ onBack, token }: { onBack: () => void; token?: string }) {
  const colors = useTheme();
  const identityInfo = useInfo();
  const [googleOpen, setGoogleOpen] = useState(false);
  const palette = profilePalette(colors);
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const methods = [
    { id: 'whatsapp', label: 'WhatsApp', icon: 'logo-whatsapp', color: palette.accent },
    { id: 'sms', label: 'SMS', icon: 'chatbubble', color: palette.link },
    // Generic mail artwork until permission for the official Gmail icon is obtained.
    { id: 'gmail', label: 'Gmail', icon: 'mail', color: palette.warmAccent },
    { id: 'identity', label: t('signInMethods.identity'), icon: 'shield-checkmark', color: palette.heading },
  ] as const;
  if (googleOpen && token) return <GoogleLinkScreen key={token} token={token} onBack={() => setGoogleOpen(false)} />;
  return (
    <View style={[styles.screen, { backgroundColor: colors.white }]}>
      <PageHeader title={t('signInMethods.title')} onBack={onBack} floating />
      <ScrollView testID="sign-in-methods-scroll"
        contentContainerStyle={{ paddingTop: screenHeaderContentInset(insets.top), paddingBottom: insets.bottom + spacing.xl }}>
        <Text style={[styles.intro, { color: colors.gray600 }]}>{t('signInMethods.intro')}</Text>
        <ProfileSection testID="sign-in-methods">
          {methods.map(method => method.id === 'gmail' && token && googleSignInEnabled() ? (
            <TouchableOpacity key="google" testID="sign-in-method-google" style={styles.row}
              accessibilityRole="button" accessibilityLabel="Google" onPress={() => setGoogleOpen(true)}>
              <View style={styles.icon} accessible={false}><Ionicons name="logo-google" size={28} color={colors.gray600} /></View>
              <Text style={[styles.label, styles.text, { color: colors.black }]}>Google</Text>
              <Ionicons name="chevron-forward" size={18} color={colors.gray600} accessible={false} />
            </TouchableOpacity>
          ) : (
            <View key={method.id} testID={`sign-in-method-${method.id}`} style={styles.row}>
              <View testID={`sign-in-method-icon-${method.id}`} style={styles.icon}
                accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
                {method.id === 'whatsapp' ? (
                  <Image testID="sign-in-method-whatsapp-logo"
                    source={require('../../assets/brands/Digital_Glyph_Green_RGB_2026.png')}
                    style={styles.logo} resizeMode="contain" accessible={false} />
                ) : (
                  <Ionicons name={method.icon} size={28} color={method.color} accessible={false} />
                )}
              </View>
              <View style={styles.text}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: colors.black }]}>{method.label}</Text>
                  {/* 7.14 / D-654 — açıklama ⓘ arkasında. */}
                  {method.id === 'identity' && (
                    <InfoButton open={identityInfo.open} onPress={identityInfo.toggle} subject={method.label} testID="sign-in-identity-info" />
                  )}
                </View>
                {method.id === 'identity' && (
                  <InfoText open={identityInfo.open} text={t('signInMethods.identityInfo')} testID="sign-in-identity-info-text" />
                )}
              </View>
            </View>
          ))}
        </ProfileSection>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  intro: { marginHorizontal: spacing.lg, marginTop: spacing.md, fontSize: fontSize.sm, lineHeight: 22 },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, minHeight: 64, paddingVertical: spacing.md, paddingHorizontal: spacing.sm },
  icon: { width: 40, height: 40, flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
  logo: { width: 30, height: 30 },
  text: { flex: 1, minWidth: 0 },
  label: { fontSize: fontSize.md, fontWeight: '500', flexShrink: 1 },
  labelRow: { flexDirection: 'row', alignItems: 'center' },
});
